import React from 'react';
import { ArrowLeft, Download, ExternalLink, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { PdfDocumentViewer } from '../../../components/PdfDocumentViewer';
import { PdfPrintButton } from '../../../components/PdfPrintButton';
import { PdfMailShareButton } from '../../../components/PdfMailShareButton';
import { isImageUrl, isOmdPdfDocument } from '../utils/omdProductView';

export type OmdMediaPreviewState =
  | { kind: 'image'; url: string; title: string }
  | { kind: 'document'; url: string; title: string; filename?: string; documentType?: string }
  | null;

type OmdMediaPreviewProps = {
  preview: OmdMediaPreviewState;
  onClose: () => void;
};

export function OmdMediaPreview({ preview, onClose }: OmdMediaPreviewProps) {
  React.useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, onClose]);

  if (!preview) return null;

  if (preview.kind === 'image') {
    return (
      <AppModal
        open
        onClose={onClose}
        overlayClassName="z-[80]"
        panelClassName="md:max-w-5xl md:w-full bg-transparent md:bg-app-surface"
        ariaLabelledBy="omd-image-preview-title"
      >
        <div className="flex flex-col h-full min-h-0 md:max-h-[92vh]">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-app-border shrink-0 bg-app-surface md:rounded-t-xl">
            <button
              type="button"
              className="md:hidden p-1.5 -ml-1 rounded-lg text-app-text-muted hover:bg-app-hover"
              onClick={onClose}
              aria-label="Zurück"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 id="omd-image-preview-title" className="flex-1 min-w-0 text-sm font-semibold text-app-text truncate">
              {preview.title}
            </h2>
            <a
              href={preview.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-app-border px-2.5 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Öffnen
            </a>
            <button
              type="button"
              className="hidden md:flex w-8 h-8 rounded-md hover:bg-app-hover text-app-text-subtle items-center justify-center"
              onClick={onClose}
              aria-label="Schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center bg-slate-900/95 md:bg-app-surface-muted p-4">
            <img
              src={preview.url}
              alt={preview.title}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-lg"
            />
          </div>
        </div>
      </AppModal>
    );
  }

  const docRef = {
    url: preview.url,
    filename: preview.filename,
    type: preview.documentType,
  };
  const isPdf = isOmdPdfDocument(docRef);
  const isImage = isImageUrl(preview.url, preview.filename);

  return (
    <AppModal
      open
      onClose={onClose}
      overlayClassName="z-[80]"
      panelClassName="md:max-w-6xl md:w-full"
      ariaLabelledBy="omd-doc-preview-title"
    >
      <div className="flex flex-col h-full min-h-0 md:max-h-[92vh]">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-app-border shrink-0">
          <button
            type="button"
            className="md:hidden p-1.5 -ml-1 rounded-lg text-app-text-muted hover:bg-app-hover"
            onClick={onClose}
            aria-label="Zurück"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 id="omd-doc-preview-title" className="flex-1 min-w-0 text-sm font-semibold text-app-text truncate">
            {preview.title}
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            {isPdf ? (
              <>
                <PdfMailShareButton
                  url={preview.url}
                  filename={preview.filename || `${preview.title}.pdf`}
                  onBeforeOpen={onClose}
                  className="inline-flex items-center gap-1 rounded-lg border border-app-border px-2.5 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                />
                <PdfPrintButton
                  url={preview.url}
                  className="inline-flex items-center gap-1 rounded-lg border border-app-border px-2.5 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                />
              </>
            ) : null}
            <a
              href={preview.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-app-border px-2.5 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Neuer Tab</span>
            </a>
            <a
              href={preview.url}
              download={preview.filename || preview.title}
              className="inline-flex items-center gap-1.5 rounded-lg bg-pipedrive-blue px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Download</span>
            </a>
            <button
              type="button"
              className="hidden md:flex w-8 h-8 rounded-md hover:bg-app-hover text-app-text-subtle items-center justify-center"
              onClick={onClose}
              aria-label="Schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 bg-app-surface-muted">
          {isImage ? (
            <div className="flex items-center justify-center p-4 min-h-[70vh]">
              <img
                src={preview.url}
                alt={preview.title}
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-sm bg-app-surface"
              />
            </div>
          ) : isPdf ? (
            <PdfDocumentViewer
              url={preview.url}
              title={preview.title}
              className="min-h-[70vh] w-full"
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 p-8 min-h-[50vh] text-center">
              <p className="text-sm text-app-text-muted max-w-md">
                Für diesen Dateityp ist keine eingebettete Vorschau verfügbar. Öffnen Sie das Dokument im Browser.
              </p>
              <a
                href={preview.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-pipedrive-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <ExternalLink className="w-4 h-4" />
                Dokument öffnen
              </a>
            </div>
          )}
        </div>
      </div>
    </AppModal>
  );
}
